/**
 * Counsel conversational consent for Sarah (Matt 2026-10-15).
 * Versioned. Typed yes = marketing consent. Spoken yes answers the
 * request only; a later inbound YES text is marketing consent.
 *
 * Do not edit lib/crm/sms-consent-text.ts or the registered 10DLC
 * campaign. This line is the Counsel ask. It does not match the
 * CI-locked SMS_CONSENT_TEXT or the A2P checkbox message_flow.
 */
import { isSmsOptOut, normalizeSmsBody } from '@/lib/crm/sms-keywords'

export const SARAH_CONSENT_WORDING_VERSION = 'sarah-consent-v1-2026-10-15'

export const SARAH_CONSENT_ASK_TEMPLATE =
  "Is it okay if Ryan Realty texts and calls you at [number], including with our AI assistant, about your request and real estate? Consent isn't required to buy anything. Msg and data rates may apply. Reply STOP to opt out, HELP for help."

export const SARAH_POST_VOICE_YES_ASK =
  'Thanks for talking with Sarah at Ryan Realty. Reply YES if it is okay for Ryan Realty to text and call you at this number, including with our AI assistant, about your request and real estate. Consent is not required to buy anything. Reply STOP to opt out, HELP for help.'

/**
 * Strict consent parse (audit W1 #37). Consent is granted ONLY when the whole
 * reply is a plain affirmative. Any negation or refusal anywhere means NOT
 * granted ("yes but don't text me", "ok no", "sure, not now"). A hedge or
 * condition ("yes but only email", "maybe", "I guess") is unclear, and unclear
 * is never a grant: the caller re-asks or records nothing.
 */
// Every phrase here starts with a word main's YES_RE already accepted
// (yes|yeah|yep|yup|sure|please|ok|okay|i agree|sounds good): this list is
// main's set or narrower, never wider (coordinator review of W1-A).
const AFFIRMATIVE_PHRASES: ReadonlySet<string> = new Set([
  'yes',
  'yeah',
  'yep',
  'yup',
  'sure',
  'ok',
  'okay',
  'i agree',
  'sounds good',
  'yes please',
  'yes you can',
  'yes that s fine',
  'yes thats fine',
  'yes it s okay',
  'yes its okay',
  'yes it s ok',
  'yes its ok',
  'sure thing',
  'sure go ahead',
  'ok sure',
  'okay sure',
  'yes i agree',
])

/** Words that make a would-be yes a no, wherever they appear. */
const REFUSAL_TOKENS: ReadonlySet<string> = new Set([
  'no', 'nope', 'nah', 'not', 'dont', 'don', 'never', 'stop', 'refuse', 'decline',
  'declined', 'cant', 'can', 'won', 'wont', 'didnt', 'doesnt', 'shouldnt', 'rather',
  'later', 'skip', 'unsubscribe', 'optout', 'cancel', 'quit', 'end', 'revoke', 'without',
])

/** Hedges and conditions: never a grant, never a recorded refusal either. */
const HEDGE_TOKENS: ReadonlySet<string> = new Set([
  'but', 'only', 'except', 'unless', 'if', 'maybe', 'perhaps', 'guess', 'probably',
  'depends', 'possibly', 'hmm', 'idk', 'unsure', 'sometimes', 'just',
])

/** Trailing courtesy that does not change a plain yes ("Yes, thanks!"). */
const TRAILING_COURTESY = /\s+(thanks|thank you|thx|ty|please|pls)$/

function consentNorm(text: string): string {
  return normalizeSmsBody(text).replace(TRAILING_COURTESY, '').trim()
}

function hasToken(text: string, set: ReadonlySet<string>): boolean {
  const n = normalizeSmsBody(text)
  if (!n) return false
  // "can't" normalizes to "can t"; "can" alone ("yes you can") is not a refusal.
  const tokens = n.split(' ')
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]
    if ((t === 'can' || t === 'won' || t === 'don') && tokens[i + 1] !== 't') continue
    if (set.has(t)) return true
    // Any n't contraction (isn't, wasn't, shouldn't) is a negation.
    if (tokens[i + 1] === 't' && t.endsWith('n')) return true
  }
  return false
}

/** Any negation, refusal or opt-out anywhere in the reply. */
function isRefusal(text: string): boolean {
  return isSmsOptOut(text) || hasToken(text, REFUSAL_TOKENS) || /\bno\s*thanks?\b/i.test(text)
}

export function sarahConsentAsk(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const ten = digits.length >= 10 ? digits.slice(-10) : digits
  const pretty =
    ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone.trim()
  return SARAH_CONSENT_ASK_TEMPLATE.replace('[number]', pretty)
}

/** True only for a plain, unhedged, un-negated yes. */
export function isConsentYes(text: string): boolean {
  if (isRefusal(text) || hasToken(text, HEDGE_TOKENS)) return false
  return AFFIRMATIVE_PHRASES.has(consentNorm(text))
}

/** True for any negation or refusal, including a yes that carries one. */
export function isConsentNo(text: string): boolean {
  return isRefusal(text)
}

export type SarahConsentChannel = 'typed' | 'spoken' | 'sms'

export type SarahConsentDecision = {
  granted: boolean
  marketing: boolean
  needsPostVoiceYes: boolean
}

/**
 * Typed yes is marketing consent. Spoken yes covers only answering the
 * request; queue a post-voice YES text before any marketing.
 * A refusal is checked first, so "yes but stop texting" is declined.
 * Returns null when unclear; null is never a grant.
 */
export function decideSarahConsent(
  reply: string,
  channel: SarahConsentChannel,
): SarahConsentDecision | null {
  const text = reply.trim()
  if (isConsentNo(text)) {
    return { granted: false, marketing: false, needsPostVoiceYes: false }
  }
  if (isConsentYes(text)) {
    const spoken = channel === 'spoken'
    return {
      granted: true,
      marketing: !spoken,
      needsPostVoiceYes: spoken,
    }
  }
  return null
}
